/**
 * src/components/agent/AgentShell.jsx — Responsive unified chat shell.
 *
 * Phase 82-03 — Chat System Unification (wave 2).
 *
 * Single responsive container composing the 82-02 hooks:
 *   - useAgentInvocationContext — seeds scope from the current view
 *   - useChat                    — conversation state, streaming, persistence
 *   - useChatList                — sidebar list + filter + archive accordion
 *
 * Mounts slots that subsequent waves fill in:
 *   - ChatSidebarV2               (82-03 Task 2 — replaces placeholder)
 *   - ChatHeader (scope chips)    (82-04)
 *   - ChatComposerV2              (82-04)
 *   - VoiceModeOverlay            (82-05)
 *   - EmptyStateSuggestions       (82-06)
 *
 * Hooks at top of body — Phase 90-07 rule.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ListChecks, Menu, X } from 'lucide-react';
import useBreakpoint from '../../hooks/useBreakpoint';
import { useChat } from '../../lib/hooks/useChat';
import { useAgentInvocationContext } from '../../lib/hooks/useAgentInvocationContext';
import { useT } from '../../lib/i18n/I18nContext';
import { useAgentMode } from '../../lib/agent/agentModeContext.jsx';
import ChatMessageList from '../shared/ChatMessageList';
import ChatSidebarV2 from './ChatSidebarV2';
import ChatHeader from './ChatHeader';
import ChatComposerV2 from './ChatComposerV2';
import VoiceModeOverlay from './VoiceModeOverlay';
import AgentModeBadge from './AgentModeBadge';
import EmptyStateSuggestions from './EmptyStateSuggestions';
import { deriveCtx } from '../../lib/agent/scopeContract';
import useQuoteCopilotChat from '../pipeline/quote/copilot/useQuoteCopilotChat';
import { TurnChangeLog } from '../pipeline/quote/copilot/CopilotBits';
import { CopilotModeChip, CopilotSuggestions, CopilotItemsPane, CopilotFillAction } from '../pipeline/quote/copilot/CopilotShellParts';
import useCaptureRouter from '../../lib/capture/useCaptureRouter';
import { ProgressCard } from '../shared/VoiceFileUploadBtn';
import { ModalSheet } from '../SharedUI';
import UploadFilesPanel from '../capture/UploadFilesPanel';
import BugReportPanel from './BugReportPanel';
import InterviewModePicker from './InterviewModePicker';
import { drainDocumentsToBrain } from '../../lib/api/knowledge';
import { logAppError } from '../../lib/errorReporter';
import {
  getShellRootStyle,
  sidebarAreaStyle,
  headerAreaStyle,
  bodyAreaStyle,
  composerAreaStyle,
  mobileDrawerBackdropStyle,
  mobileDrawerPanelStyle,
  hamburgerBtnStyle,
  closeBtnStyle,
  placeholderStyle,
} from './AgentShell.styles';

export default function AgentShell({
  view = 'agent',
  mode = 'page',
  initialConversationId = null,
  ctxOverride = null,
  // { entries: ScopeEntry[], nonce } — a host asks to add context chips to the RUNNING
  // chat (Command Center "Ask the assistant about this task"). Applied once per nonce
  // through the same setScope the "Add scope" picker uses (deduped + persisted, one write).
  scopeRequest = null,
  onClose,
  className,
}) {
  const { tier } = useBreakpoint();
  const { t, locale } = useT();
  const invocation = useAgentInvocationContext({ override: ctxOverride });

  const [currentId, setCurrentId] = useState(initialConversationId);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  // TICKET-1479/1484: interview mode -- null | 'bug' | 'idea' | 'help'. The AI interviewer
  // replaces the list + composer (also inside Quote Copilot).
  const [bugMode, setBugMode] = useState(null);

  // F4 (2026-04-21) — mutual exclusion. When dictation starts it
  // enters 'capture' mode; if the conversation overlay is still open,
  // close it. Unmount → endSession → VAD + TTS + SSE teardown. This
  // is what turns the AgentMode signal into actual UI behavior.
  const { mode: agentMode } = useAgentMode();
  useEffect(() => {
    if (agentMode === 'capture' && voiceModeOpen) {
      setVoiceModeOpen(false);
    }
  }, [agentMode, voiceModeOpen]);
  // Tracks whether the "hydrate currentId from chat.sessionId" effect has
  // already fired. Without this gate, clicking "New chat" (setCurrentId(null))
  // immediately triggered the effect to restore currentId to the previous
  // conversation's sessionId — so the user's "new chat" appeared to "bounce
  // back" to the existing conversation (2026-04-20 report).
  const hydratedFromSessionRef = useRef(false);

  const generalChat = useChat({
    conversationId: currentId,
    initialScope: invocation.seed,
    view: invocation.view || view,
  });

  // TICKET-912: Quote Copilot mode. Opened on a deal (FAB on a deal, or a copilot
  // launcher) the bot talks to the deal's quote copilot -- same shell, composer and
  // message list; the chip switches back to general chat. One thread per deal.
  const copilotCtx = ctxOverride?.dealId ? ctxOverride : null;
  const [copilotOn, setCopilotOn] = useState(() => !!copilotCtx && ctxOverride?.copilot !== false);
  const copilotChat = useQuoteCopilotChat({
    dealId: copilotCtx?.dealId,
    dealName: copilotCtx?.dealName,
    quoteId: copilotCtx?.quoteId || null,
    onBeforeTurn: copilotCtx?.onBeforeTurn,
    onChanged: copilotCtx?.onChanged,
    enabled: !!copilotCtx && copilotOn,
  });
  const inCopilot = !!copilotCtx && copilotOn;
  // TICKET-912 slice 4: the attach button is a WikiLLM entry point -- any file goes
  // through the same router as the deal/project Upload button (image -> photos,
  // audio -> UVTP, text -> note, PDF/Office/CAD -> vault). Opened from a deal, the
  // upload is scoped to it; the general bot lets the vault's confirm sheet pick.
  const capture = useCaptureRouter({
    contextType: copilotCtx ? 'deal' : 'general',
    contextId: copilotCtx?.dealId || null,
  });
  // A filed document reaches the copilot as brain chips; run the bounded drain now
  // instead of waiting for the daily cron (kill switch document_brain_extract).
  const onVaultFiled = useCallback(() => {
    if (!copilotCtx) return;
    drainDocumentsToBrain(5).catch((err) => logAppError({ type: 'capture_drain_failed', message: err?.message, view: 'AgentShell', metadata: { step: 'agent_shell.drain_documents', deal_id: copilotCtx.dealId } }));
  }, [copilotCtx]);
  const chat = inCopilot ? copilotChat : generalChat;

  const appliedScopeNonce = useRef(null);
  // ONE scope update per request: setScope persists the whole scope each time, so
  // adding task + project one by one fired two writes that could land out of order.
  const setGeneralScope = generalChat.setScope;
  useEffect(() => {
    if (!scopeRequest?.entries?.length || appliedScopeNonce.current === scopeRequest.nonce) return;
    appliedScopeNonce.current = scopeRequest.nonce;
    const entries = scopeRequest.entries.filter((e) => e?.type && e?.id);
    if (entries.length) setGeneralScope((prev) => [...prev, ...entries]);
  }, [scopeRequest, setGeneralScope]);

  useEffect(() => {
    // Run ONCE on first mount if we never received an initialConversationId
    // and the hook has a cached sessionId (e.g., from a refresh mid-session).
    // After that, honor explicit user actions (handleNewChat / handleSelect).
    if (hydratedFromSessionRef.current) return;
    if (currentId) { hydratedFromSessionRef.current = true; return; }
    if (chat.sessionId) {
      hydratedFromSessionRef.current = true;
      setCurrentId(chat.sessionId);
    }
  }, [currentId, chat.sessionId]);

  useEffect(() => {
    if (mode === 'modal' && typeof document !== 'undefined') {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mode]);

  const handleNewChat = useCallback(() => {
    setCurrentId(null);
    setSidebarOpen(false);
  }, []);

  const handleSelect = useCallback(conv => {
    setCurrentId(conv.id);
    setSidebarOpen(false);
  }, []);

  const handleSend = useCallback(async () => {
    const text = chat.input?.trim();
    if (!text) return;
    await chat.send(text, {
      onConversationCreated: id => {
        setCurrentId(id);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('chat-conversation-updated'));
        }
      },
    });
  }, [chat]);

  // 'panel' (Command Center split view) is a pane inside a page, not the page: like
  // 'modal' it is too narrow for the inline conversation list, which moves to the drawer.
  const isDesktop = tier === 'desktop' && mode === 'page';
  const isTablet = tier === 'tablet' && mode === 'page';
  const showInlineSidebar = isDesktop || isTablet;

  // TICKET-107 (a) — the chat's selected project (same derivation the
  // VoiceModeOverlay wiring uses below) drives the sidebar's scope filter.
  const scopeProjectId =
    (chat?.scope || []).find((e) => e?.type === 'project')?.id || null;

  const sidebarNode = inCopilot ? (
    <CopilotItemsPane
      items={copilotChat.items}
      error={copilotChat.itemsError}
      onCopy={copilotChat.copyForEmail}
      onClose={!showInlineSidebar ? () => setSidebarOpen(false) : undefined}
    />
  ) : (
    <ChatSidebarV2
      currentConversationId={currentId}
      onSelectConversation={handleSelect}
      onNewConversation={handleNewChat}
      onClose={!showInlineSidebar ? () => setSidebarOpen(false) : undefined}
      selectedProjectId={scopeProjectId}
    />
  );

  return (
    <div
      style={getShellRootStyle(tier, mode)}
      className={className}
      data-testid="agent-shell"
      data-mode={mode}
      data-tier={tier}
    >
      {showInlineSidebar && <div style={sidebarAreaStyle}>{sidebarNode}</div>}

      <div style={{
        ...headerAreaStyle,
        gridColumn: showInlineSidebar ? '2 / 3' : '1 / 2',
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 0,
        // TICKET-075-UI — the chat top bar (menu / title / close) sat flush at
        // y=0 and collided with the iPhone Dynamic Island / status bar (CEO
        // 2026-06-10 screenshot). Pad the header by the top safe-area inset, with
        // a max() floor so it still clears the status bar when the inset resolves
        // to 0 (black-translucent PWA mode). Mirrors the Voice Mode overlay fix +
        // satisfies the check-safe-area-top sentinel. Header bg fills the inset.
        // A 'panel' never touches the top edge (the view header sits above it), so the
        // safe-area inset would only add a 47px gap on iPhone.
        paddingTop: mode === 'panel' ? 8 : 'max(8px, env(safe-area-inset-top))',
        gap: 0,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 0,
        }}>
          {!showInlineSidebar && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              style={{ ...hamburgerBtnStyle, marginLeft: 8, ...(inCopilot ? { width: 'auto', padding: '0 8px', gap: 2 } : {}) }}
              // TICKET-912: in copilot mode the drawer holds the deal's open items, not chats.
              aria-label={inCopilot ? t('quoteCopilot.itemsTab') : t('agentChat.openChatList')}
              data-testid={inCopilot ? 'copilot-items-open' : 'agent-shell-menu'}
            >
              {inCopilot ? <ListChecks size={20} /> : <Menu size={20} />}
              {inCopilot && copilotChat.openCount > 0 && (
                <span data-testid="copilot-items-open-count" style={{ marginLeft: 2, fontSize: 12, fontWeight: 700, color: 'var(--warn)' }}>
                  {copilotChat.openCount}
                </span>
              )}
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            {!inCopilot && <ChatHeader chat={chat} />}
            {inCopilot && (
              <CopilotModeChip on dealName={copilotCtx.dealName} onToggle={() => setCopilotOn(false)} />
            )}
            {/* F5 — mode badge. Auto-hides in idle; shows 'Voice' pill
                in conversation or 'Capturing' pill in capture. Lives
                inline with the header so it's visible whether the
                overlay is open or closed. */}
            <AgentModeBadge />
          </div>
          {mode === 'modal' && (
            <button
              type="button"
              onClick={onClose}
              style={{ ...closeBtnStyle, marginRight: 8 }}
              aria-label="Close chat"
              data-testid="agent-shell-close"
            >
              <X size={20} />
            </button>
          )}
        </div>
        {/* General chat on a deal: the way back to Quote Copilot gets its OWN row. In the
            header row the general ChatHeader squeezed it to 24px under the close button
            (live check at 375px, TICKET-912) -- untappable. */}
        {/* TICKET-912 slice 4 (CEO: one action -- fill every bundle from all sources and raise
            the questions). Its own row: the header has no room at 375px (see below). The empty
            thread already offers it as a suggestion. */}
        {inCopilot && !bugMode && chat.messages.length > 0 && (
          <div data-testid="copilot-action-row" style={{ display: 'flex', padding: '4px 8px 8px', minWidth: 0 }}>
            <CopilotFillAction onSend={(text) => copilotChat.send(text)} disabled={chat.loading} />
          </div>
        )}
        {/* TICKET-1484: Bug / Idea / Help, in general chat AND Quote Copilot (where the staff work).
            Placed AFTER the copilot action row so that row is never displaced. */}
        {!bugMode && (
          <div data-testid="bug-report-entry-row" style={{ display: 'flex', padding: inCopilot ? '0 8px 8px' : '0 8px 6px', minWidth: 0 }}>
            <InterviewModePicker onSelect={setBugMode} />
          </div>
        )}
        {copilotCtx && !inCopilot && (
          <div data-testid="copilot-mode-row" style={{ display: 'flex', padding: '4px 8px 8px', minWidth: 0 }}>
            <CopilotModeChip on={false} dealName={copilotCtx.dealName} onToggle={() => setCopilotOn(true)} />
          </div>
        )}
      </div>

      <div style={{
        ...bodyAreaStyle,
        gridColumn: showInlineSidebar ? '2 / 3' : '1 / 2',
      }}>
        {bugMode ? (
          <BugReportPanel
            key={bugMode}
            kind={bugMode}
            route={typeof window !== 'undefined' ? window.location.pathname : null}
            onExit={() => setBugMode(null)}
          />
        ) : inCopilot ? (
          copilotChat.loaded && chat.messages.length === 0 && !chat.loading && !chat.error ? (
            <CopilotSuggestions onSend={(text) => copilotChat.send(text)} />
          ) : (
            <ChatMessageList
              messages={chat.messages}
              /* the live progress bubble IS the busy state (slice 2b) -- no second typing indicator */
              loading={false}
              error={chat.error}
              agentType="assistant"
              renderAgentExtra={(msg) => (
                <TurnChangeLog
                  msg={msg}
                  reverted={copilotChat.reverted.has(msg.id)}
                  busy={copilotChat.revertingId !== null}
                  onRevert={copilotChat.revert}
                />
              )}
            />
          )
        ) : chat.hydrating ? (
          <div style={placeholderStyle}>Loading conversation...</div>
        ) : chat.messages.length === 0 ? (
          <EmptyStateSuggestions
            ctx={deriveCtx(chat.scope)}
            scope={chat.scope}
            onFillInput={text => chat.setInput(text)}
            onInstantSend={text => chat.send(text, {
              onConversationCreated: id => {
                setCurrentId(id);
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('chat-conversation-updated'));
                }
              },
            })}
          />
        ) : (
          <ChatMessageList
            messages={chat.messages}
            loading={chat.loading}
            error={chat.error}
          />
        )}
      </div>

      <div style={{
        ...composerAreaStyle,
        gridColumn: showInlineSidebar ? '2 / 3' : '1 / 2',
        // TICKET-1479: the bug-report panel brings its own input row.
        ...(bugMode ? { display: 'none' } : {}),
      }}>
        {capture.uploads.length > 0 && (
          <div data-testid="agent-shell-uploads" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 12px 6px' }}>
            {capture.uploads.map((u) => (
              <ProgressCard key={u.uploadId} upload={u} onDismiss={() => capture.dismissUpload(u.uploadId)} />
            ))}
          </div>
        )}
        <ChatComposerV2
          chat={chat}
          conversationId={currentId}
          onSend={() => handleSend()}
          onStop={chat.stop}
          /* TICKET-912 slice 3: voice mode everywhere -- in Quote Copilot mode it talks to
             the deal's copilot (VoiceModeOverlay copilot prop), and above the quote builder
             the overlay is lifted over the builder portal. */
          onVoiceMode={() => setVoiceModeOpen(true)}
          onLocalCommand={(handler) => {
            if (handler === 'clear-messages' && typeof chat.clearMessages === 'function') {
              chat.clearMessages();
            } else if (handler === 'assign-project' && !showInlineSidebar) {
              setSidebarOpen(false);
            }
          }}
          onFileSelect={capture.processFiles}
        />
      </div>

      {/* Documents: the vault panel (classify -> scope confirm -> file), pre-scoped to
          the deal when the bot was opened from one. */}
      {capture.vaultQueue && (
        <ModalSheet open onClose={capture.closeVault} title={t('vaultCapture.dropTitle')}>
          <div data-testid="agent-shell-vault-sheet">
            <UploadFilesPanel initialFiles={capture.vaultQueue} context={capture.vaultContext} onFiled={onVaultFiled} />
          </div>
        </ModalSheet>
      )}

      {!showInlineSidebar && sidebarOpen && (
        <>
          <div
            style={mobileDrawerBackdropStyle}
            onClick={() => setSidebarOpen(false)}
            role="button"
            tabIndex={0}
            aria-label={t('agentChat.closeChatList')}
            onKeyDown={e => {
              if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSidebarOpen(false);
              }
            }}
          />
          <div style={mobileDrawerPanelStyle} role="dialog" aria-label="Chat list">
            {sidebarNode}
          </div>
        </>
      )}

      {/* F1 (2026-04-21) — conditionally mount. Previously rendered
          unconditionally as <VoiceModeOverlay open={voiceModeOpen} />,
          which kept the overlay's useVoiceConversation + useVoiceAutoMode
          hook chain alive in the background. The always-listening VAD
          hijacked dictation audio and fired ghost conversation turns
          (Chris's 2026-04-21 Puppeteer repro: "interrupted by voice to
          speech" while dictating). Conditional mount means React
          unmount tears down mic streams, SSE controllers, and the
          TTS AudioContext on close — guaranteed by useEffect cleanup. */}
      {voiceModeOpen && (
        <VoiceModeOverlay
          open={true}
          onClose={() => setVoiceModeOpen(false)}
          /* Quote Copilot keeps its own per-deal thread: no general chat conversation. */
          conversationId={inCopilot ? null : currentId}
          copilot={inCopilot ? {
            dealId: copilotCtx.dealId,
            quoteId: copilotCtx.quoteId || null,
            onTurnDone: () => { copilotChat.refresh(); copilotCtx.onChanged?.(); },
          } : null}
          zIndex={ctxOverride?.aboveBuilder ? 10030 : null}
          /* 90-07 AC-1 — voice inherits chat scope. scope is an array of
             ScopeEntry {type, id}; find the first project/deal entry and
             pass its id. If scope has none, pass null (voice_context_missing
             event fires on mount for observability). */
          contextProjectId={
            (chat?.scope || []).find((e) => e?.type === 'project')?.id || null
          }
          contextDealId={
            (chat?.scope || []).find((e) => e?.type === 'deal')?.id || null
          }
          /* 2026-04-21 persistence fix — when voice creates a new chat
             conversation (currentId was null on open), subscribe the
             AgentShell chat view to it immediately so closing the overlay
             leaves the user looking at the same conversation that has
             their voice turns. Previously the turns landed in an orphan
             "Voice session" row invisible to the composer. */
          onConversationPersisted={(cid) => {
            if (cid && cid !== currentId) setCurrentId(cid);
          }}
        />
      )}
    </div>
  );
}
